import { exhaustive } from "exhaustive"
import { FC, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { TabbedSubView, TabbedSubViewMenuItem } from "../../Shared/TabbedSubView/TabbedSubView"
import { ConsumerContactPersons } from "../ConsumerContactPersons/ConsumerContactPersons"
import {
	CustomerPortalConsumerInformation,
	GetConsumer,
} from "../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation"
import { CustomerPortalConsumerInvoicingInformation } from "../CustomerPortalConsumerInvoicingInformation/CustomerPortalConsumerInvoicingInformation"
import { Users } from "../Users/Users"
import style from "./CustomerPortalConsumerDetails.module.css"

type CustomerPortalConsumerDetailsProps = {
	consumerId: string
}

enum ConsumerTabs {
	ConsumerDetails = "ConsumerDetails",
	UsersAndContacts = "UsersAndContacts",
}

export const CustomerPortalConsumerDetails: FC<CustomerPortalConsumerDetailsProps> = ({ consumerId }) => {
	const auth = useAuth()
	const client = useClient()
	const permissions = usePermissions()

	const [queryParams, setQueryParams] = useSearchParams()

	const [showUsers, setShowUsers] = useState<boolean>(true)
	const [consumer, setConsumer] = useState<GetConsumer | "loading" | null>("loading")

	useEffect(() => {
		if (!permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View) || !consumerId) {
			setConsumer(null)
			return
		}

		fetchConsumer()
	}, [client.identifier, consumerId, permissions])

	const currentSelectedTab: ConsumerTabs = !!queryParams.get("tab")
		? (queryParams.get("tab") as ConsumerTabs)
		: ConsumerTabs.ConsumerDetails

	useEffect(() => {
		if (
			!queryParams.get("tab") ||
			(!!queryParams.get("tab") && !Object.values(ConsumerTabs).includes(queryParams.get("tab") as any))
		) {
			queryParams.set("tab", ConsumerTabs.ConsumerDetails)
			setQueryParams(queryParams, { replace: true })
		}
	}, [])

	const menuItems = useMemo<TabbedSubViewMenuItem[]>(() => {
		return [
			{
				id: ConsumerTabs.ConsumerDetails,
				name: "Översikt",
				onClick: () => {},
				disabled: false,
				hidden: false,
				queryParamIdValue: null,
			},
			{
				id: ConsumerTabs.UsersAndContacts,
				name: "Användare och kontaktpersoner",
				onClick: () => {},
				disabled: false,
				hidden: !client.features.allowKlarnaIntegration,
				queryParamIdValue: null,
			},
		]
	}, [client, permissions])

	function fetchConsumer() {
		API.getWithRetries<GetConsumer>(
			`/customer-portal/consumers-v1/${client.identifier}/details/${consumerId}`,
			true,
			undefined,
			10,
		)
			.then((res) => {
				setConsumer(res)
			})
			.catch(() => {
				setConsumer(null)
			})
	}

	function canViewTab(tab: ConsumerTabs): boolean {
		return exhaustive(tab, {
			[ConsumerTabs.ConsumerDetails]: () => {
				return true
			},
			[ConsumerTabs.UsersAndContacts]: () => {
				return client.features.allowKlarnaIntegration
			},

			_: () => null,
		})
	}

	function tabContent() {
		if (!canViewTab(currentSelectedTab)) {
			return null
		}

		return exhaustive(currentSelectedTab, {
			[ConsumerTabs.ConsumerDetails]: () => {
				if (!consumerId) {
					return null
				}

				return (
					<div className={style.flexColumns}>
						{consumer && consumer !== "loading" ? (
							<CustomerPortalConsumerInformation
								consumer={consumer}
								onInactivate={() => {
									setShowUsers(false)
									setTimeout(() => {
										setShowUsers(true)
									}, 15)
								}}
								onConsumerUpdated={() => {
									auth.refreshMeData()
									fetchConsumer()
								}}
							/>
						) : null}

						{consumer === "loading" ? (
							<div className={style.box}>
								<Loader />
							</div>
						) : null}

						<CustomerPortalConsumerInvoicingInformation consumerId={consumerId} />
					</div>
				)
			},
			[ConsumerTabs.UsersAndContacts]: () => {
				return (
					<div className={style.flexColumns}>
						{showUsers ? (
							<Users type={"Consumer"} clientIdentifier={client.identifier} consumerId={consumerId} />
						) : null}
						<ConsumerContactPersons consumerId={consumerId} />
					</div>
				)
			},

			_: () => null,
		})
	}

	if (consumer === null || consumer === "loading") {
		return (
			<TabbedSubView profilePicture={null} name={""} infoBoxes={[]} menuItems={menuItems} cancelAction={null}>
				<Loader />
			</TabbedSubView>
		)
	}

	return (
		<TabbedSubView
			profilePicture={{ showBox: true, src: consumer.profilePictureUrl }}
			name={consumer.consumerDetails.name}
			infoBoxes={[]}
			menuItems={menuItems}
			cancelAction={null}>
			{tabContent()}
		</TabbedSubView>
	)
}
